import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { Card, CardContent, CardHeader, Container, Grid, MenuItem } from '@mui/material';
import { Block } from 'src/components/Block';
import AppButton from 'src/components/common/AppButton';
import AppLoadingAndErrorWrapper from 'src/components/common/AppLoadingAndErrorWrapper';
import { ConfirmDialog } from 'src/components/custom-dialog';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useLocales } from 'src/locales';
import useAddNewADS from './use-add-new-ads';
import { Helmet } from 'react-helmet-async';
import { AdsTypeFilter } from 'src/constants/ads-type-filter';
import { Upload } from 'src/components/upload';
import Image from 'src/components/image';
import RHFDatePicker from 'src/components/hook-form/rhf-date-picker';
import moment from 'moment';
import RHFSwitch from 'src/components/hook-form/rhf-switch';
import { RHFSelect } from 'src/components/hook-form/rhf-select';
import { AdsAdByType } from 'src/constants/ads-adBy-type';

const AddNewADS = () => {
  const {
    editAds,
    adsId,
    methods,
    handleSubmit,
    onSubmit,
    isEditing,
    confirm,
    isAdding,
    isUpdating,
    mutate,
    file,
    isUploadingImage,
    handleDropSingleFile,
    setFile,
    sessionsOpts,
    isLoadingSessions,
    hearingCouncilsOpts,
    isLoadingHearingCouncils,
  } = useAddNewADS();
  const { t } = useLocales();

  return (
    <>
      <Helmet>
        <title>{t('titles.addAds')}</title>
      </Helmet>

      <Container>
        {/* <AppLoadingAndErrorWrapper
          isLoading={isLoadingAds}
          errorMessage={null}
          sx={{ height: 'auto' }}
        > */}
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item container xs={6}>
              <Grid item xs={12} display="flex" gap={4} flexDirection="column">
                <Block label={t('tables.ads.headLabels.adBy')}>
                  <RHFSelect name="adBy">
                    {AdsAdByType(t).map((adBy) => (
                      <MenuItem key={adBy.value} value={adBy.value}>
                        {adBy.label}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Block>

                <Block label={t('tables.ads.headLabels.type')}>
                  <RHFSelect name="type">
                    <MenuItem key="notSelection" value="notSelection">
                      {t('tables.ads.headLabels.notSelection')}
                    </MenuItem>
                    {AdsTypeFilter(t).map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Block>
                {methods.watch('type') == 'session' && (
                  <Block label={t('tables.ads.headLabels.sessionName')}>
                    {/* <RHFAutocomplete name="refIdName" valueName="refId" options={sessionsOpts} /> */}
                    <RHFSelect
                      name="refId"
                      onChange={(e) => {
                        const selectedOption = sessionsOpts?.find(
                          (opt) => opt.value === e.target.value
                        );
                        methods.setValue('startDate', selectedOption?.startDate);
                        methods.setValue('endDate', selectedOption?.endDate);
                      }}
                    >
                      {sessionsOpts?.map((session) => (
                        <MenuItem key={session.value} value={session.value}>
                          {session.label}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Block>
                )}
                {methods.watch('type') == 'hearingCouncil' && (
                  <Block label={t('tables.ads.headLabels.hearingCouncil')}>
                    <RHFSelect
                      name="refId"
                      onChange={(e) => {
                        const selectedOption = hearingCouncilsOpts?.find(
                          (opt) => opt.value === e.target.value
                        );
                        methods.setValue('startDate', selectedOption?.startDate);
                        methods.setValue('endDate', selectedOption?.endDate);
                      }}
                    >
                      {hearingCouncilsOpts?.map((hearingCouncil) => (
                        <MenuItem key={hearingCouncil.value} value={hearingCouncil.value}>
                          {hearingCouncil.label}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Block>
                )}

                <Block label={t('tables.ads.headLabels.startDate')}>
                  <RHFDatePicker
                    name="startDate"
                    dateAndTime
                    minDate={moment(new Date()).toDate()}
                  />
                </Block>
                <Block label={t('tables.ads.headLabels.endDate')}>
                  <RHFDatePicker name="endDate" dateAndTime />
                </Block>
                <Block label={t('tables.ads.headLabels.description')}>
                  <RHFTextField
                    name="description"
                    multiline
                    minRows={4}
                    inputProps={{ maxLength: 100 }}
                  />
                </Block>
                <Block label={t('tables.ads.headLabels.active')}>
                  <RHFSwitch name="active" label="" />
                </Block>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Card>
                <CardHeader title={t('dialogs.addNewKnowledgeBase.uploadImage')} />
                <CardContent>
                  <AppLoadingAndErrorWrapper
                    sx={{ height: 'auto' }}
                    isLoading={isUploadingImage || isUpdating}
                    errorMessage={null}
                  >
                    <Upload
                      file={file}
                      onDrop={handleDropSingleFile}
                      uploadLabel="dialogs.addNewKnowledgeBase.uploadImage"
                      fileFieldName="mediaUrl"
                      error={!!methods.formState.errors.mediaUrl}
                      onDelete={() => {
                        setFile(null);
                        methods.setValue('mediaUrl', '');
                        editAds({
                          id: adsId,
                          ...methods.watch(),
                        });
                      }}
                      previewComponent={<Image src={methods.watch().mediaUrl} />}
                    />
                  </AppLoadingAndErrorWrapper>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <AppButton
                label={isEditing ? t('buttons.edit') : t('buttons.add')}
                isLoading={false}
                type="submit"
                fullWidth
              />
            </Grid>
          </Grid>
        </FormProvider>
        {/* </AppLoadingAndErrorWrapper> */}
      </Container>
      {/* Confirm */}
      <ConfirmDialog
        open={confirm.value}
        onClose={(event: React.SyntheticEvent<Element, Event>, reason: string) => {
          if (reason && reason === 'backdropClick') {
            confirm.onFalse();
          }
        }}
        content={t('dialogs.confirmMessage', {
          editOrAdd: isEditing ? t('buttons.edit').toLowerCase() : t('buttons.add').toLowerCase(),
        })}
        icon={<BorderColorOutlinedIcon fontSize="large" sx={{ mx: 'auto' }} />}
        action={
          <>
            <AppButton
              fullWidth
              label={t('publicWords.no')}
              variant="outlined"
              onClick={() => {
                confirm.onFalse();
              }}
            />
            <AppButton
              fullWidth
              label={t('publicWords.yes')}
              isLoading={isAdding || isUpdating}
              onClick={mutate}
            />
          </>
        }
      />
    </>
  );
};

export default AddNewADS;
